// ============================================================================
// CLIENT GROUP SERVICE
// ============================================================================
// Business logic and HTTP calls for the ClientGroup master.
//
// Backend wraps every response in ApiResponse<T> with this shape:
//   { success, message, data, errors, correlationId, timestamp }
//
// List endpoints additionally wrap data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// The frontend grids (SyutGrid) consume PaginatedResponse<T> with shape:
//   { success, message, data: T[], pagination: { currentPage, pageSize, totalPages, totalItems, hasNextPage, hasPreviousPage } }
//
// `getAll()` MUST do this transformation — see CLAUDE.md "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { clientGroupEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  ClientGroup,
  CreateClientGroupRequest,
  UpdateClientGroupRequest,
  ClientGroupSearchRequest,
} from '../types';

class ClientGroupService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: ClientGroupSearchRequest
  ): Promise<PaginatedResponse<ClientGroup>> {
    const endpoint = params
      ? buildEndpointWithQuery(clientGroupEndpoints.basePath(), params as Record<string, any>)
      : clientGroupEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ClientGroup>> {
    const response = await get<ApiResponse<ClientGroup>>(
      clientGroupEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ClientGroup>> {
    const response = await get<ApiResponse<ClientGroup>>(
      clientGroupEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateClientGroupRequest
  ): Promise<ApiResponse<ClientGroup>> {
    const response = await post<ApiResponse<ClientGroup>>(
      clientGroupEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateClientGroupRequest
  ): Promise<ApiResponse<ClientGroup>> {
    const response = await put<ApiResponse<ClientGroup>>(
      clientGroupEndpoints.update(id),
      data
    );
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(clientGroupEndpoints.delete(id));
    return { success: true };
  }
}

export const clientGroupService = new ClientGroupService();
export default clientGroupService;
